import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { saveUserCart, getUserCart } from "../services/userService";

const CartContext = createContext();

export const useCart = () => {
  return useContext(CartContext);
};

export const CartProvider = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();

  const [cartItems, setCartItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // 🔹 LOAD CART
  useEffect(() => {
    const loadCart = async () => {
      if (authLoading) return;

      try {
        if (currentUser) {
          // Load cart from Firestore
          const savedCart = await getUserCart(currentUser.uid);

          if (savedCart && savedCart.length > 0) {
            setCartItems(savedCart);
          } else {
            setCartItems([]);
          }
        } else {
          // Guest cart
          const localCart = JSON.parse(localStorage.getItem("cart") || "[]");
          setCartItems(localCart);
        }
      } catch (error) {
        console.error("Error loading cart:", error);
      }

      setIsLoading(false);
    };

    loadCart();
  }, [currentUser, authLoading]);

  // 🔹 SAVE CART
  useEffect(() => {
    if (authLoading || isLoading) return;

    if (currentUser) {
      // Save cart to Firestore
      saveUserCart(currentUser.uid, cartItems).catch((error) => {
        console.error("Error saving cart:", error);
      });
    } else {
      // Save guest cart
      localStorage.setItem("cart", JSON.stringify(cartItems));
    }
  }, [cartItems, currentUser, authLoading, isLoading]);

  // ➕ Add Item
  const addToCart = (product) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find(
        (item) => item._id === product._id
      );

      if (existingItem) {
        return prevItems.map((item) =>
          item._id === product._id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }

      return [...prevItems, { ...product, quantity: 1 }];
    });
  };

  // ❌ Remove Item
  const removeFromCart = (id) => {
    setCartItems((prevItems) =>
      prevItems.filter((item) => item._id !== id)
    );
  };

  // 🔄 Update Quantity
  const updateQuantity = (id, quantity) => {
    if (quantity <= 0) {
      removeFromCart(id);
      return;
    }

    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item._id === id ? { ...item, quantity } : item
      )
    );
  };

  // 🧹 Clear Cart
  const clearCart = () => {
    setCartItems([]);
  };

  // 💰 Total Price
  const getTotalPrice = () => {
    return cartItems.reduce(
      (total, item) => total + item.price * item.quantity,
      0
    );
  };

  // 🛒 Cart Count
  const getCartCount = () => {
    return cartItems.reduce(
      (count, item) => count + item.quantity,
      0
    );
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotalPrice,
        getCartCount,
        isLoading,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};